import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useState,
  ReactElement,
} from "react";
import { TAuthContext, TCredentials, TUser } from "../types/context";
import {
  decodeUser,
  getCredentials,
  removeCredentials,
  setCredentials,
} from "../utils/helpers";
import useGet from "../hooks/common/useGet";
import { API_USERS } from "../utils/apis";

const AuthContext = createContext<TAuthContext>({
  loggedIn: false,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
  user: null,
  gettingAuth: true,
  authToken: "",
});

const AuthProvider = ({ children }: { children: ReactElement }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<TUser | null>(null);
  const [authToken, setAuthToken] = useState<string>("");
  const [gettingAuth, setGettingAuth] = useState(true);
  const { loadData: getUserData, data: userData } = useGet();

  useEffect(() => {
    const fetchCredentials = () => {
      setGettingAuth(true);
      const res = getCredentials();

      if (res) {
        setLoggedIn(true);
        setUser(decodeUser(res.access));
        setAuthToken(res.access);
      }
      setGettingAuth(false);
    };

    fetchCredentials();
  }, []);

  useEffect(() => {
    if (loggedIn && userData && !user) {
      updateUser(userData);
    }
  }, [userData, loggedIn, user]);

  const login = (credentials: TCredentials) => {
    const currentUser = decodeUser(credentials.access);
    setCredentials(credentials);
    getUserData({
      id: currentUser?.id,
      api: API_USERS,
      bearerToken: credentials.access,
    });

    setUser(currentUser);
    setAuthToken(credentials.access);
    setLoggedIn(true);
  };

  const logout = () => {
    removeCredentials();
    setUser(null);
    setLoggedIn(false);
  };

  const updateUser = (user: TUser) => {
    setGettingAuth(true);
    setCredentials({ access: authToken, user });
    setUser(user);
    setGettingAuth(false);
  };

  const renderBody = (ele: ReactElement) => cloneElement(ele);

  return (
    <AuthContext.Provider
      value={{
        login,
        loggedIn: loggedIn,
        logout,
        user,
        gettingAuth,
        authToken,
        updateUser,
      }}
    >
      {renderBody(children)}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const LogoutRoute = () => <>{useAuth().logout()}</>;

export { AuthProvider, useAuth, LogoutRoute };
